<!--@format-->
<template>
    <div class="review-and-sign-popup">
        <section class="popup-header">
            <div>
                <h1 class="title">Review and Sign Document</h1>
                <p class="document-name">{{ element.documentName }}</p>
            </div>
            <div class="logo-container">
                <div v-if="element.companyLogo" class="logo" :style="cssProps"></div>
                <div class="created-with">
                    <p>Created with:</p>
                    <img :src="glassHiveLogo" alt="GlassHive logo" />
                </div>
            </div>
        </section>
        <section class="popup-body">
            <section class="sender-data">
                <app-profile-image
                    :src="element.sender.profileImageUrl | profileImagePlaceholder"
                />
                <div class="personal-information">
                    <p class="sender-name">{{ element.sender.name }}</p>
                    <p>({{ element.sender.email }})</p>
                </div>
            </section>
            <section class="sender-message">
                <div class="speech-tip"></div>
                <div class="speech-bubble">
                    <froalaView :value="element.sender.message" class="sender-message" />
                </div>
            </section>
        </section>
        <section class="popup-footer">
            <div class="agreement-information">
                <p>
                    Please read our
                    <router-link :to="`${baseUrl}/Account/PrivacyPolicy`" target="_blank">
                        Privacy Policy
                    </router-link>
                    and
                    <router-link
                        :to="`${baseUrl}/Document/Sign/${documentGuid}/disclosure`"
                        target="_blank"
                    >
                        Signature Disclosure
                    </router-link>
                </p>
                <div class="checkbox-wrapper">
                    <app-checkbox v-model="privacyPolicyAgreement" />
                    <p>I agree to use electronic records and signatures.</p>
                </div>
            </div>
            <div class="actions">
                <app-select-dropdown
                    v-model="roles"
                    :items="options"
                    :menu-props="menuProps"
                    placeholder="Options"
                    @input="handleEmitAction"
                />
                <app-button
                    class="continue-btn"
                    :disabled="!privacyPolicyAgreement"
                    @click="handleEmitAction"
                >
                    Continue
                </app-button>
            </div>
        </section>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const AppSelectDropdown = () => import('@/components/common/app-select-dropdown');

import EventBus from '@/scripts/event-bus';

export default {
    name: 'review-and-sign-popup',

    components: {
        'app-button': AppButton,
        'app-profile-image': AppProfileImage,
        'app-checkbox': AppCheckbox,
        'app-select-dropdown': AppSelectDropdown,
    },

    props: {
        element: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            privacyPolicyAgreement: false,
            roles: null,
            glassHiveLogo: require('@/assets/images/SVG/horizontal-glasshive-simplified.svg'),
            menuProps: { bottom: true, offsetY: true },
            viewportWidth: window.innerWidth,
            options: [
                {
                    id: 1,
                    name: 'Read Document',
                    action: 'read',
                },

                {
                    id: 2,
                    name: 'Delegate Signing to Other',
                    action: 'delegate',
                },

                {
                    id: 3,
                    name: 'Decline to Sign',
                    action: 'decline',
                },
            ],
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        documentGuid() {
            return this.$route.params.guid;
        },

        cssProps() {
            return {
                '--logo-image': `url(${this.element.companyLogo})`,
            };
        },
    },

    watch: {
        viewportWidth: {
            immediate: true,
            handler(value) {
                if (value <= 690) {
                    this.menuProps = { top: true, offsetY: true };
                } else {
                    this.menuProps = { bottom: true, offsetY: true };
                }
            },
        },
    },

    mounted() {
        window.addEventListener('resize', this.handleViewportChange);
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.handleViewportChange);
    },

    methods: {
        handleViewportChange() {
            this.viewportWidth = window.innerWidth;
        },

        handleEmitAction() {
            const eventPayload = {
                action: this.roles ? this.roles.action : 'read',
                acceptedAgreement: this.privacyPolicyAgreement,
            };
            EventBus.$emit('document:signer-action', eventPayload);
            this.$emit('close');
        },
    },
};
</script>

<style>
.review-and-sign-popup {
    height: 100%;
    width: 100%;
}

.review-and-sign-popup .popup-header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap-reverse;
    gap: 0 50px;
}

.review-and-sign-popup .popup-header .logo-container .logo {
    background: var(--logo-image);
    background-repeat: no-repeat;
    background-size: contain;
    height: 56px;
    margin-bottom: 8px;
    width: auto;
}

.review-and-sign-popup .popup-header .title {
    margin-bottom: 8px;
    line-height: 25px;
}

.review-and-sign-popup .popup-header .logo-container {
    display: block;
}

.review-and-sign-popup .popup-header .logo-container .created-with {
    display: flex;
    gap: 8px;
    text-align: left;
    font: normal normal normal 14px/17px Roboto;
}

.review-and-sign-popup .popup-header .logo-container .created-with img {
    height: 17px;
    width: 79px;
    object-fit: contain;
}

.review-and-sign-popup .popup-header .document-name {
    font: normal normal normal 20px/25px Nunito;
    margin-bottom: 24px;
}

.review-and-sign-popup .popup-body {
    padding-bottom: 40px;
    border-bottom: 2px solid #e9e9ef;
}

.review-and-sign-popup .popup-body .sender-data {
    display: flex;
    flex-wrap: wrap;
    gap: 0 16px;
}

.review-and-sign-popup .popup-body .sender-data .app-profile-image .background-image {
    height: 64px;
    width: 64px;
}

.review-and-sign-popup .popup-body .sender-data .personal-information {
    text-align: left;
    font: normal normal normal 14px/17px Roboto;
}

.review-and-sign-popup .popup-body .sender-data .personal-information p {
    margin: 0px;
}

.review-and-sign-popup .popup-body .sender-data .personal-information .sender-name {
    font: normal normal bold 20px/24px Roboto;
}

.review-and-sign-popup .popup-body .sender-message .speech-tip {
    background: #f7f7f8 0% 0% no-repeat padding-box;
    clip-path: polygon(50% 30%, 0% 100%, 100% 100%);
    height: 8px;
    margin-left: 24px;
    width: 16px;
}

.review-and-sign-popup .popup-body .sender-message .speech-bubble {
    background: #f7f7f8 0% 0% no-repeat padding-box;
    border-radius: 16px;
    text-align: left;
    font: normal normal normal 16px/25px Nunito;
    width: 100%;
    padding: 16px 16px 21px 16px;
}

.review-and-sign-popup .popup-body .sender-message .speech-bubble .sender-message > * {
    margin: 0;
    text-align: left;
    font: normal normal normal 16px/25px Nunito;
}

.review-and-sign-popup .popup-footer {
    color: #707070;
    display: flex;
    flex-wrap: wrap;
    gap: 16px 8px;
    justify-content: space-between;
    text-align: left;
    font: normal normal normal 14px/17px Roboto;
    padding-top: 24px;
}

.review-and-sign-popup .popup-footer .actions {
    display: flex;
    gap: 16px;
}

.review-and-sign-popup .popup-footer .actions .app-button {
    height: 40px;
    width: 120px;
}

.review-and-sign-popup .popup-footer .app-select-dropdown {
    max-width: 128px;
    height: 40px;
}

.review-and-sign-popup .popup-footer .app-select.v-input > .v-input__control > .v-input__slot {
    min-height: 40px;
}

.review-and-sign-popup .popup-footer .app-select-dropdown .v-menu__content {
    max-width: 190px;
}

.review-and-sign-popup
    .popup-footer
    .app-select-dropdown
    .v-menu__content
    .v-list-item
    .v-list-item__title {
    font: normal normal normal 12px/14px Roboto;
}

.review-and-sign-popup .popup-footer .checkbox-wrapper {
    display: flex;
}

.review-and-sign-popup .popup-footer .checkbox-wrapper p {
    margin-left: 8px;
}

/*Media Queries for Mobile view*/
@media only screen and (max-width: 800px) {
    .review-and-sign-popup .popup-header {
        justify-content: center;
    }

    .review-and-sign-popup .popup-header .title,
    .review-and-sign-popup .popup-header .document-name {
        text-align: center;
    }

    .review-and-sign-popup .popup-footer {
        justify-content: center;
    }
}

@media only screen and (max-width: 550px) {
    .review-and-sign-popup .popup-body .sender-data {
        justify-content: center;
    }

    .review-and-sign-popup .popup-body .sender-data .personal-information {
        text-align: center;
    }

    .review-and-sign-popup .popup-body .sender-message .speech-tip {
        display: none;
    }
}
</style>
